import {
  type BrowserDisplaySession,
  type BrowserId,
  BrowserDisplayClientId,
  type BrowserDisplayCaptureMode,
  type BrowserDisplayIceCandidate,
  type BrowserSessionId,
  type BrowserTabId,
  type EnvironmentId,
  type ThreadId,
  type BrowserUserId,
} from "@cmmd-forge/contracts";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { ensureEnvironmentApi } from "~/environmentApi";
import { cn, randomUUID } from "~/lib/utils";
import {
  browserDisplayAttachOptionalFields,
  browserDisplayIceServersShouldForceClientRelay,
  browserDisplayWithReconnect,
  browserErrorIsWebRtcNegotiationTimeout,
  browserIceCandidateType,
  browserIceServersFromKey,
  browserIceServersKey,
  browserStreamHasLiveVideoTrack,
  browserVideoElementHasRenderableStream,
  browserWebRtcAttachFailureDisplay,
  browserWebRtcConnectingDisplay,
  browserWebRtcIceDiagnosticsDetail,
  browserWebRtcNegotiationTimeoutDisplay,
  browserWebRtcNetworkFailureDisplay,
  browserWebRtcNoTrackDisplay,
  browserWebRtcNonRenderableTrackDisplay,
  browserWebRtcReconnectingDisplay,
  browserWebRtcShouldFailNegotiationTimeout,
  browserWebRtcShouldScheduleConnectivityTimeout,
  browserWebRtcUnsupportedDisplay,
  browserWebRtcVideoAspectRatio,
  browserWebRtcVideoClassState,
  resolveBrowserWebRtcReconnectDecision,
  resolveBrowserWebRtcDevicePixelRatio,
  toRtcIceCandidate,
  toRtcIceServers,
  withBrowserWebRtcNegotiationTimeout,
} from "./RemoteWebRtcBrowserSurface.logic";

interface RemoteWebRtcBrowserSurfaceProps {
  readonly active: boolean;
  /**
   * Which region of the runtime display this surface streams. Omitted means
   * `browser`, which crops Chromium's chrome and is what every existing caller
   * gets — the attach payload is byte-identical when this is not passed.
   */
  readonly captureMode?: BrowserDisplayCaptureMode | undefined;
  readonly environmentId: EnvironmentId;
  readonly threadId: ThreadId;
  readonly browserId: BrowserId | null;
  readonly sessionId: BrowserSessionId | null;
  readonly tabId: BrowserTabId | null;
  readonly userId: BrowserUserId | null;
  readonly viewport?: { readonly width: number; readonly height: number } | undefined;
  readonly devicePixelRatio?: number | undefined;
  readonly iceServers?: BrowserDisplaySession["iceServers"] | undefined;
  readonly remoteIceCandidatesByClientId?:
    | Record<string, ReadonlyArray<BrowserDisplayIceCandidate | null>>
    | undefined;
  readonly negotiationTimeoutMs?: number | undefined;
  readonly connectivityTimeoutMs?: number | undefined;
  readonly trackTimeoutMs?: number | undefined;
  readonly maxReconnectAttempts?: number | undefined;
  readonly reconnectBaseDelayMs?: number | undefined;
  readonly reconnectMaxDelayMs?: number | undefined;
  readonly onDisplayUpdated: (display: BrowserDisplaySession) => void;
  readonly onError: (error: unknown) => void;
  /**
   * The stream's intrinsic frame size, or null while nothing is rendering.
   *
   * Only a caller that maps screen coordinates back onto the stream needs this
   * — the Desktop surface, which turns a click in the panel into a click on the
   * runtime's display. The element's own box cannot answer that: it is
   * letterboxed against a display whose aspect ratio it does not share.
   */
  readonly onVideoSizeChange?:
    | ((size: { readonly width: number; readonly height: number } | null) => void)
    | undefined;
}

interface AttachedVideoStream {
  readonly generation: number;
  readonly stream: MediaStream;
}

type BrowserWebRtcDebugEntry = {
  readonly timestamp: string;
  readonly event: string;
  readonly details: Record<string, unknown>;
};

function browserWebRtcDebugEnabled(): boolean {
  try {
    return (
      new URLSearchParams(window.location.search).get("browserRtcDebug") === "1" ||
      window.localStorage.getItem("forge.browserRtcDebug") === "1"
    );
  } catch {
    return false;
  }
}

function browserWebRtcDebug(event: string, details: Record<string, unknown> = {}): void {
  if (!browserWebRtcDebugEnabled()) {
    return;
  }
  const entry: BrowserWebRtcDebugEntry = {
    timestamp: new Date().toISOString(),
    event,
    details,
  };
  const debugWindow = window as typeof window & {
    __forgeBrowserWebRtcDebug?: BrowserWebRtcDebugEntry[] | undefined;
  };
  debugWindow.__forgeBrowserWebRtcDebug = [
    ...(debugWindow.__forgeBrowserWebRtcDebug ?? []).slice(-199),
    entry,
  ];
  console.debug("[forge-browser-webrtc]", event, details);
}

// Cloud display attach may include cold Sprite/browser runtime recovery plus
// server-side WebRTC media setup. Keep this longer than the server attach
// stage timeout so the client does not fail a valid slow answer first.
export const WEBRTC_NEGOTIATION_TIMEOUT_MS = 75_000;
const WEBRTC_CONNECTIVITY_TIMEOUT_MS = 45_000;
const WEBRTC_TRACK_TIMEOUT_MS = 8_000;
const WEBRTC_DISCONNECTED_TIMEOUT_MS = 3_000;
const WEBRTC_RECONNECT_MAX_ATTEMPTS = 5;
const WEBRTC_RECONNECT_BASE_DELAY_MS = 750;
const WEBRTC_RECONNECT_MAX_DELAY_MS = 5_000;

function requirePeerConnection(): typeof RTCPeerConnection {
  if (typeof RTCPeerConnection === "undefined") {
    throw new Error("This browser does not support RTCPeerConnection.");
  }
  return RTCPeerConnection;
}

function preferVp8ReceiveCodec(transceiver: RTCRtpTransceiver): void {
  if (
    typeof RTCRtpReceiver === "undefined" ||
    typeof RTCRtpReceiver.getCapabilities !== "function" ||
    typeof transceiver.setCodecPreferences !== "function"
  ) {
    return;
  }
  const vp8Codecs =
    RTCRtpReceiver.getCapabilities("video")?.codecs.filter(
      (codec) => codec.mimeType.toLowerCase() === "video/vp8",
    ) ?? [];
  if (vp8Codecs.length === 0) {
    return;
  }
  transceiver.setCodecPreferences(vp8Codecs);
}

/**
 * Push the stream's intrinsic size to the caller, but only when it changes.
 *
 * `syncActiveVideoElement` runs on a 250ms interval as well as on media events,
 * so an unconditional call would re-render the consumer four times a second for
 * a value that changes once per stream.
 */
function reportVideoSize(
  reportedKeyRef: { current: string | null },
  onSizeChange:
    | ((size: { readonly width: number; readonly height: number } | null) => void)
    | undefined,
  videoElement: HTMLVideoElement | null,
): void {
  const size =
    videoElement && videoElement.videoWidth > 0 && videoElement.videoHeight > 0
      ? { width: videoElement.videoWidth, height: videoElement.videoHeight }
      : null;
  const key = size ? `${size.width}x${size.height}` : "none";
  if (reportedKeyRef.current === key) return;
  reportedKeyRef.current = key;
  onSizeChange?.(size);
}

export function RemoteWebRtcBrowserSurface(props: RemoteWebRtcBrowserSurfaceProps) {
  const {
    active,
    browserId,
    connectivityTimeoutMs = WEBRTC_CONNECTIVITY_TIMEOUT_MS,
    devicePixelRatio,
    environmentId,
    iceServers,
    maxReconnectAttempts = WEBRTC_RECONNECT_MAX_ATTEMPTS,
    negotiationTimeoutMs = WEBRTC_NEGOTIATION_TIMEOUT_MS,
    onDisplayUpdated,
    onError,
    onVideoSizeChange,
    reconnectBaseDelayMs = WEBRTC_RECONNECT_BASE_DELAY_MS,
    reconnectMaxDelayMs = WEBRTC_RECONNECT_MAX_DELAY_MS,
    remoteIceCandidatesByClientId,
    captureMode,
    sessionId,
    tabId,
    threadId,
    trackTimeoutMs = WEBRTC_TRACK_TIMEOUT_MS,
    userId,
    viewport,
  } = props;
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const peerRef = useRef<RTCPeerConnection | null>(null);
  const trackReceivedRef = useRef(false);
  const appliedRemoteIceCountRef = useRef(0);
  const seenRemoteIceCandidateTypeCountsRef = useRef(new Map<string, number>());
  const remoteDescriptionAcceptedRef = useRef(false);
  const pendingRemoteIceCandidatesRef = useRef<Array<BrowserDisplayIceCandidate | null>>([]);
  const attachGenerationRef = useRef(0);
  const playbackAttemptedStreamRef = useRef<MediaStream | null>(null);
  const onDisplayUpdatedRef = useRef(onDisplayUpdated);
  const onErrorRef = useRef(onError);
  const onVideoSizeChangeRef = useRef(onVideoSizeChange);
  const reportedVideoSizeRef = useRef<string | null>(null);
  const latestViewportRef = useRef(viewport);
  const latestTabIdRef = useRef(tabId);
  const [attachedVideoStream, setAttachedVideoStream] = useState<AttachedVideoStream | null>(null);
  const [videoElementHasActiveStream, setVideoElementHasActiveStream] = useState(false);
  const [clientId, setClientId] = useState<BrowserDisplayClientId | null>(null);
  const [reconnectGeneration, setReconnectGeneration] = useState(0);
  const reconnectAttemptRef = useRef(0);
  const iceServersKey = browserIceServersKey(iceServers);
  const viewportKey = viewport ? `${viewport.width}x${viewport.height}` : "no-viewport";
  const tabPresenceKey = tabId ? "has-tab" : "no-tab";
  const resolvedDevicePixelRatio = resolveBrowserWebRtcDevicePixelRatio(devicePixelRatio);
  const devicePixelRatioKey = resolvedDevicePixelRatio
    ? String(resolvedDevicePixelRatio)
    : "no-device-pixel-ratio";
  const latestDevicePixelRatioRef = useRef(resolvedDevicePixelRatio);
  const activeVideoStream =
    attachedVideoStream?.generation === attachGenerationRef.current
      ? attachedVideoStream.stream
      : null;
  onDisplayUpdatedRef.current = onDisplayUpdated;
  onErrorRef.current = onError;
  onVideoSizeChangeRef.current = onVideoSizeChange;
  latestViewportRef.current = viewport;
  latestTabIdRef.current = tabId;
  latestDevicePixelRatioRef.current = resolvedDevicePixelRatio;
  const videoClassState = browserWebRtcVideoClassState({
    hasActiveVideoStream: Boolean(activeVideoStream),
    videoElementHasActiveStream,
  });

  useEffect(() => {
    reconnectAttemptRef.current = 0;
  }, [
    active,
    browserId,
    devicePixelRatioKey,
    iceServersKey,
    sessionId,
    tabPresenceKey,
    viewportKey,
  ]);

  useEffect(() => {
    if (activeVideoStream && videoElementHasActiveStream) {
      reconnectAttemptRef.current = 0;
    }
  }, [activeVideoStream, videoElementHasActiveStream]);

  const syncActiveVideoElement = useCallback(() => {
    const videoElement = videoRef.current;
    if (!videoElement) {
      reportVideoSize(reportedVideoSizeRef, onVideoSizeChangeRef.current, null);
      setVideoElementHasActiveStream(false);
      return;
    }
    if (!activeVideoStream || !browserStreamHasLiveVideoTrack(activeVideoStream)) {
      if (videoElement.srcObject) {
        videoElement.srcObject = null;
      }
      playbackAttemptedStreamRef.current = null;
      reportVideoSize(reportedVideoSizeRef, onVideoSizeChangeRef.current, null);
      setVideoElementHasActiveStream(false);
      return;
    }
    if (videoElement.srcObject !== activeVideoStream) {
      videoElement.srcObject = activeVideoStream;
    }
    if (playbackAttemptedStreamRef.current !== activeVideoStream) {
      playbackAttemptedStreamRef.current = activeVideoStream;
      void videoElement.play().catch((error: unknown) => {
        if (videoRef.current?.srcObject === activeVideoStream) {
          onErrorRef.current(error);
        }
      });
    }
    const isRenderable = browserVideoElementHasRenderableStream(videoElement, activeVideoStream);
    reportVideoSize(
      reportedVideoSizeRef,
      onVideoSizeChangeRef.current,
      isRenderable ? videoElement : null,
    );
    setVideoElementHasActiveStream((current) =>
      current === isRenderable ? current : isRenderable,
    );
  }, [activeVideoStream]);

  useLayoutEffect(() => {
    syncActiveVideoElement();
  }, [syncActiveVideoElement]);

  useEffect(() => {
    syncActiveVideoElement();
    if (!activeVideoStream) {
      return;
    }
    const handleTrackEnded = () => {
      const videoElement = videoRef.current;
      if (videoElement?.srcObject === activeVideoStream) {
        videoElement.srcObject = null;
      }
      setVideoElementHasActiveStream(false);
      setAttachedVideoStream((current) => (current?.stream === activeVideoStream ? null : current));
    };
    const videoElement = videoRef.current;
    const handleVideoRenderStateChanged = () => {
      syncActiveVideoElement();
    };
    videoElement?.addEventListener("loadedmetadata", handleVideoRenderStateChanged);
    videoElement?.addEventListener("resize", handleVideoRenderStateChanged);
    videoElement?.addEventListener("playing", handleVideoRenderStateChanged);
    videoElement?.addEventListener("emptied", handleVideoRenderStateChanged);
    for (const track of activeVideoStream.getVideoTracks()) {
      track.addEventListener("ended", handleTrackEnded, { once: true });
    }
    const syncInterval = window.setInterval(syncActiveVideoElement, 250);
    return () => {
      window.clearInterval(syncInterval);
      videoElement?.removeEventListener("loadedmetadata", handleVideoRenderStateChanged);
      videoElement?.removeEventListener("resize", handleVideoRenderStateChanged);
      videoElement?.removeEventListener("playing", handleVideoRenderStateChanged);
      videoElement?.removeEventListener("emptied", handleVideoRenderStateChanged);
      for (const track of activeVideoStream.getVideoTracks()) {
        track.removeEventListener("ended", handleTrackEnded);
      }
    };
  }, [activeVideoStream, syncActiveVideoElement]);

  useEffect(() => {
    if (!clientId || !peerRef.current) {
      return;
    }
    const candidates = remoteIceCandidatesByClientId?.[clientId] ?? [];
    const peer = peerRef.current;
    const nextCandidates = candidates.slice(appliedRemoteIceCountRef.current);
    appliedRemoteIceCountRef.current = candidates.length;
    for (const candidate of nextCandidates) {
      if (candidate) {
        const candidateType = browserIceCandidateType(candidate);
        seenRemoteIceCandidateTypeCountsRef.current.set(
          candidateType,
          (seenRemoteIceCandidateTypeCountsRef.current.get(candidateType) ?? 0) + 1,
        );
      }
      if (!remoteDescriptionAcceptedRef.current) {
        pendingRemoteIceCandidatesRef.current.push(candidate);
        continue;
      }
      void peer.addIceCandidate(toRtcIceCandidate(candidate)).catch((error: unknown) => {
        // Ignore failures from a peer connection that has since been replaced
        // (reattach/teardown can race ahead of this async candidate apply);
        // surfacing them to onError would report a spurious error for a
        // connection the user is no longer using.
        if (peerRef.current !== peer) {
          return;
        }
        onErrorRef.current(error);
      });
    }
  }, [clientId, remoteIceCandidatesByClientId]);

  useEffect(() => {
    const nextGeneration = attachGenerationRef.current + 1;
    attachGenerationRef.current = nextGeneration;
    setAttachedVideoStream(null);
    setVideoElementHasActiveStream(false);
    trackReceivedRef.current = false;
    remoteDescriptionAcceptedRef.current = false;
    setClientId(null);
    appliedRemoteIceCountRef.current = 0;
    seenRemoteIceCandidateTypeCountsRef.current = new Map();
    pendingRemoteIceCandidatesRef.current = [];
    const latestTabId = latestTabIdRef.current;
    if (!active || !browserId || !sessionId || !latestTabId) {
      browserWebRtcDebug("inactive", {
        active,
        browserId: browserId ?? null,
        sessionId: sessionId ?? null,
        tabId: latestTabId ?? null,
      });
      return;
    }

    const abortController = new AbortController();
    const activeBrowserId = browserId;
    const activeSessionId = sessionId;
    const attachTabId: BrowserTabId = latestTabId;
    const nextClientId = BrowserDisplayClientId.make(randomUUID());
    const activeViewport = latestViewportRef.current;
    const activeDevicePixelRatio = latestDevicePixelRatioRef.current;
    const activeIceServers = browserIceServersFromKey(iceServersKey);
    const videoElement = videoRef.current;
    let peer: RTCPeerConnection | null = null;
    let attachDeadlineTimeout: number | undefined;
    let connectivityTimeout: number | undefined;
    let trackTimeout: number | undefined;
    let disconnectedTimeout: number | undefined;
    let reconnectTimeout: number | undefined;
    let failed = false;
    let attachAccepted = false;
    let acceptedDisplay: BrowserDisplaySession | null = null;
    let attachedTrackStream: MediaStream | null = null;
    let submittedLocalIceCandidateCount = 0;
    let submittedLocalConcreteIceCandidateCount = 0;
    let submittedLocalEndOfCandidatesCount = 0;
    const submittedLocalIceCandidateTypeCounts = new Map<string, number>();
    const pendingLocalIceCandidates: Array<BrowserDisplayIceCandidate | null> = [];
    setClientId(nextClientId);
    browserWebRtcDebug("effect-started", {
      browserId: activeBrowserId,
      sessionId: activeSessionId,
      tabId: attachTabId,
      clientId: nextClientId,
      viewport: activeViewport ?? null,
      devicePixelRatio: activeDevicePixelRatio ?? null,
      iceServers: activeIceServers?.map((server) => ({
        urls: server.urls,
        hasUsername: Boolean(server.username),
        hasCredential: server.credential !== undefined,
      })),
    });

    const clearAttachDeadlineTimeout = () => {
      if (attachDeadlineTimeout !== undefined) {
        window.clearTimeout(attachDeadlineTimeout);
        attachDeadlineTimeout = undefined;
      }
    };
    const clearTrackTimeout = () => {
      if (trackTimeout !== undefined) {
        window.clearTimeout(trackTimeout);
        trackTimeout = undefined;
      }
    };
    const clearConnectivityTimeout = () => {
      if (connectivityTimeout !== undefined) {
        window.clearTimeout(connectivityTimeout);
        connectivityTimeout = undefined;
      }
    };
    const clearDisconnectedTimeout = () => {
      if (disconnectedTimeout !== undefined) {
        window.clearTimeout(disconnectedTimeout);
        disconnectedTimeout = undefined;
      }
    };
    const clearReconnectTimeout = () => {
      if (reconnectTimeout !== undefined) {
        window.clearTimeout(reconnectTimeout);
        reconnectTimeout = undefined;
      }
    };
    const networkFailureDetail = (targetPeer: RTCPeerConnection) =>
      browserWebRtcIceDiagnosticsDetail({
        prefix: "Remote WebRTC browser display ICE failed before media could route.",
        connectionState: targetPeer.connectionState,
        iceConnectionState: targetPeer.iceConnectionState,
        localCandidatesSubmitted: submittedLocalIceCandidateCount,
        localCandidatesReal: submittedLocalConcreteIceCandidateCount,
        localEndOfCandidates: submittedLocalEndOfCandidatesCount,
        localCandidateTypeCounts: submittedLocalIceCandidateTypeCounts,
        remoteCandidatesSeen: appliedRemoteIceCountRef.current,
        remoteCandidateTypeCounts: seenRemoteIceCandidateTypeCountsRef.current,
        remoteCandidatesPending: pendingRemoteIceCandidatesRef.current.length,
      });
    const closeCurrentPeer = (targetPeer?: RTCPeerConnection | null) => {
      if (peerRef.current === targetPeer || peerRef.current === peer) {
        peerRef.current = null;
      }
      targetPeer?.close();
      browserWebRtcDebug("peer-close", {
        clientId: nextClientId,
        targetConnectionState: targetPeer?.connectionState ?? null,
        targetIceConnectionState: targetPeer?.iceConnectionState ?? null,
      });
      if (targetPeer !== peer) {
        peer?.close();
      }
      if (videoRef.current?.srcObject === attachedTrackStream) {
        videoRef.current.srcObject = null;
      }
      if (videoElement) {
        videoElement.srcObject = null;
      }
      attachedTrackStream = null;
      setAttachedVideoStream(null);
      setVideoElementHasActiveStream(false);
    };
    const scheduleReconnectOrFail = (
      display: BrowserDisplaySession,
      targetPeer?: RTCPeerConnection,
      error?: unknown,
    ) => {
      if (abortController.signal.aborted || failed) {
        return;
      }
      failed = true;
      browserWebRtcDebug("schedule-reconnect-or-fail", {
        clientId: nextClientId,
        status: display.status,
        reason: display.reason ?? null,
        detail: display.detail ?? null,
        currentAttempt: reconnectAttemptRef.current,
      });
      clearAttachDeadlineTimeout();
      clearConnectivityTimeout();
      clearTrackTimeout();
      clearDisconnectedTimeout();
      clearReconnectTimeout();
      closeCurrentPeer(targetPeer ?? peer);
      const reconnectDecision = resolveBrowserWebRtcReconnectDecision({
        currentAttempt: reconnectAttemptRef.current,
        maxReconnectAttempts,
        baseDelayMs: reconnectBaseDelayMs,
        maxDelayMs: reconnectMaxDelayMs,
      });
      if (reconnectDecision.kind === "exhausted") {
        onDisplayUpdatedRef.current(
          browserDisplayWithReconnect(display, {
            status: "exhausted",
            attempt: reconnectDecision.attempt,
          }),
        );
        if (error !== undefined) {
          onErrorRef.current(error);
        }
        return;
      }
      reconnectAttemptRef.current = reconnectDecision.attempt;
      onDisplayUpdatedRef.current(
        browserWebRtcReconnectingDisplay({
          viewport: display.viewport ?? activeViewport,
          devicePixelRatio: display.devicePixelRatio ?? activeDevicePixelRatio,
          iceServers: display.iceServers ?? activeIceServers,
          reason: display.reason,
          detail: display.detail ?? "Remote WebRTC browser display is reconnecting.",
          attempt: reconnectDecision.attempt,
          delayMs: reconnectDecision.delayMs,
        }),
      );
      reconnectTimeout = window.setTimeout(() => {
        if (!abortController.signal.aborted) {
          setReconnectGeneration((generation) => generation + 1);
        }
      }, reconnectDecision.delayMs);
    };
    const failWithNetworkUnreachable = (targetPeer?: RTCPeerConnection) => {
      scheduleReconnectOrFail(
        browserWebRtcNetworkFailureDisplay({
          viewport: activeViewport,
          devicePixelRatio: activeDevicePixelRatio,
          iceServers: activeIceServers,
          ...(targetPeer ? { detail: networkFailureDetail(targetPeer) } : {}),
        }),
        targetPeer,
      );
    };
    const failWithNegotiationTimeout = () => {
      if (
        !browserWebRtcShouldFailNegotiationTimeout({
          aborted: abortController.signal.aborted,
          failed,
          trackReceived: trackReceivedRef.current,
        })
      ) {
        return;
      }
      scheduleReconnectOrFail(
        browserWebRtcNegotiationTimeoutDisplay({
          viewport: activeViewport,
          devicePixelRatio: activeDevicePixelRatio,
          iceServers: activeIceServers,
        }),
        peer ?? undefined,
      );
    };
    const failWithConnectivityTimeout = (targetPeer: RTCPeerConnection) => {
      if (
        !browserWebRtcShouldScheduleConnectivityTimeout({
          aborted: abortController.signal.aborted,
          failed,
          attachAccepted,
          hasAcceptedDisplay: Boolean(acceptedDisplay),
          connectionState: targetPeer.connectionState,
          iceConnectionState: targetPeer.iceConnectionState,
        })
      ) {
        return;
      }
      scheduleReconnectOrFail(
        browserWebRtcNetworkFailureDisplay({
          viewport: acceptedDisplay?.viewport ?? activeViewport,
          devicePixelRatio: acceptedDisplay?.devicePixelRatio ?? activeDevicePixelRatio,
          iceServers: acceptedDisplay?.iceServers ?? activeIceServers,
          detail: browserWebRtcIceDiagnosticsDetail({
            prefix:
              "Remote WebRTC browser display accepted an answer, but ICE did not connect before media timeout.",
            connectionState: targetPeer.connectionState,
            iceConnectionState: targetPeer.iceConnectionState,
            localCandidatesSubmitted: submittedLocalIceCandidateCount,
            localCandidatesReal: submittedLocalConcreteIceCandidateCount,
            localEndOfCandidates: submittedLocalEndOfCandidatesCount,
            localCandidateTypeCounts: submittedLocalIceCandidateTypeCounts,
            remoteCandidatesSeen: appliedRemoteIceCountRef.current,
            remoteCandidateTypeCounts: seenRemoteIceCandidateTypeCountsRef.current,
            remoteCandidatesPending: pendingRemoteIceCandidatesRef.current.length,
          }),
        }),
        targetPeer,
      );
    };
    const sendLocalIceCandidate = async (candidate: BrowserDisplayIceCandidate | null) => {
      const api = ensureEnvironmentApi(environmentId);
      if (!api.browser) {
        throw new Error("Browser runtime is not available for this environment.");
      }
      submittedLocalIceCandidateCount += 1;
      if (candidate) {
        submittedLocalConcreteIceCandidateCount += 1;
        const candidateType = browserIceCandidateType(candidate);
        submittedLocalIceCandidateTypeCounts.set(
          candidateType,
          (submittedLocalIceCandidateTypeCounts.get(candidateType) ?? 0) + 1,
        );
      } else {
        submittedLocalEndOfCandidatesCount += 1;
      }
      await api.browser.addDisplayIceCandidate({
        threadId,
        browserId: activeBrowserId,
        sessionId: activeSessionId,
        tabId: attachTabId,
        clientId: nextClientId,
        ...(userId ? { userId } : {}),
        ...(captureMode ? { captureMode } : {}),
        candidate,
      });
      browserWebRtcDebug("local-ice-submitted", {
        clientId: nextClientId,
        candidateType: candidate ? browserIceCandidateType(candidate) : "end",
        submittedLocalIceCandidateCount,
        submittedLocalConcreteIceCandidateCount,
        submittedLocalEndOfCandidatesCount,
      });
    };
    const handleLocalIceCandidate = (candidate: BrowserDisplayIceCandidate | null) => {
      if (!attachAccepted) {
        pendingLocalIceCandidates.push(candidate);
        return;
      }
      void sendLocalIceCandidate(candidate).catch((error: unknown) => {
        if (!abortController.signal.aborted) {
          onErrorRef.current(error);
        }
      });
    };
    const flushLocalIceCandidates = () => {
      for (const candidate of pendingLocalIceCandidates.splice(0)) {
        void sendLocalIceCandidate(candidate).catch((error: unknown) => {
          if (!abortController.signal.aborted) {
            onErrorRef.current(error);
          }
        });
      }
    };
    const flushRemoteIceCandidates = async (targetPeer: RTCPeerConnection) => {
      const pendingRemoteIceCandidates = pendingRemoteIceCandidatesRef.current.splice(0);
      for (const candidate of pendingRemoteIceCandidates) {
        browserWebRtcDebug("remote-ice-flush", {
          clientId: nextClientId,
          candidateType: candidate ? browserIceCandidateType(candidate) : "end",
        });
        // A stale buffered candidate that werift/the peer rejects after the SDP
        // answer was already accepted is not fatal: tearing the healthy peer down
        // (by letting this throw up into attach().catch → scheduleReconnectOrFail)
        // would force an unnecessary full reconnect. Mirror the live remote-ICE
        // path (apply effect above): ignore failures from a replaced peer, and
        // route a real failure on the current peer to onError without aborting
        // the flush.
        await targetPeer.addIceCandidate(toRtcIceCandidate(candidate)).catch((error: unknown) => {
          if (peerRef.current !== targetPeer) {
            return;
          }
          onErrorRef.current(error);
        });
      }
    };
    const scheduleConnectivityTimeout = (targetPeer: RTCPeerConnection) => {
      clearConnectivityTimeout();
      if (
        !browserWebRtcShouldScheduleConnectivityTimeout({
          aborted: abortController.signal.aborted,
          failed,
          attachAccepted,
          hasAcceptedDisplay: Boolean(acceptedDisplay),
          connectionState: targetPeer.connectionState,
          iceConnectionState: targetPeer.iceConnectionState,
        })
      ) {
        return;
      }
      connectivityTimeout = window.setTimeout(
        () => failWithConnectivityTimeout(targetPeer),
        connectivityTimeoutMs,
      );
    };
    const scheduleMediaHealthTimeout = (targetPeer: RTCPeerConnection) => {
      clearTrackTimeout();
      if (abortController.signal.aborted || failed || !attachAccepted || !acceptedDisplay) {
        return;
      }
      const stream = attachedTrackStream;
      if (stream) {
        trackTimeout = window.setTimeout(() => {
          const currentVideoElement = videoRef.current;
          if (abortController.signal.aborted || failed || attachedTrackStream !== stream) {
            return;
          }
          if (
            currentVideoElement &&
            browserVideoElementHasRenderableStream(currentVideoElement, stream)
          ) {
            return;
          }
          scheduleReconnectOrFail(
            browserWebRtcNonRenderableTrackDisplay({
              viewport: acceptedDisplay?.viewport ?? activeViewport,
              devicePixelRatio: acceptedDisplay?.devicePixelRatio ?? activeDevicePixelRatio,
              iceServers: acceptedDisplay?.iceServers ?? activeIceServers,
            }),
            targetPeer,
          );
        }, trackTimeoutMs);
        return;
      }
      trackTimeout = window.setTimeout(() => {
        if (abortController.signal.aborted || failed || trackReceivedRef.current) {
          return;
        }
        scheduleReconnectOrFail(
          browserWebRtcNoTrackDisplay({
            viewport: acceptedDisplay?.viewport ?? activeViewport,
            devicePixelRatio: acceptedDisplay?.devicePixelRatio ?? activeDevicePixelRatio,
            iceServers: acceptedDisplay?.iceServers ?? activeIceServers,
          }),
          targetPeer,
        );
      }, trackTimeoutMs);
    };

    /*
     * `attach` is over the CRAP threshold and was already over it before this
     * change: on origin/main it measured cyclomatic 20 / cognitive 17 / CRAP
     * 106.4. Threading the capture mode through it and hoisting the optional
     * attach fields into `browserDisplayAttachOptionalFields` brought it to
     * cyclomatic 17 / cognitive 14 / CRAP 79.4 — strictly better on all three.
     *
     * The audit's new-only gate keys a finding partly on which thresholds it
     * exceeds, so dropping cognitive below its threshold retires the inherited
     * finding and files the remainder as introduced. Suppressed rather than
     * either inflating the function back over the cognitive line or
     * decomposing the whole WebRTC negotiation inside a feature PR — CRAP 30 at
     * this coverage tier needs cyclomatic 8, which is a rewrite of the
     * negotiation path, not a tidy-up. `stale-suppressions` will flag this line
     * when that rewrite lands.
     */
    // fallow-ignore-next-line complexity
    async function attach() {
      const api = ensureEnvironmentApi(environmentId);
      if (!api.browser) {
        throw new Error("Browser runtime is not available for this environment.");
      }

      let PeerConnection: typeof RTCPeerConnection;
      try {
        PeerConnection = requirePeerConnection();
      } catch {
        browserWebRtcDebug("unsupported-peerconnection", { clientId: nextClientId });
        onDisplayUpdatedRef.current(
          browserWebRtcUnsupportedDisplay({
            viewport: activeViewport,
            devicePixelRatio: activeDevicePixelRatio,
            iceServers: activeIceServers,
          }),
        );
        return;
      }
      onDisplayUpdatedRef.current(
        browserWebRtcConnectingDisplay({
          viewport: activeViewport,
          devicePixelRatio: activeDevicePixelRatio,
          iceServers: activeIceServers,
        }),
      );
      attachDeadlineTimeout = window.setTimeout(failWithNegotiationTimeout, negotiationTimeoutMs);
      const nextPeer = new PeerConnection({
        iceServers: toRtcIceServers(activeIceServers),
        iceTransportPolicy: browserDisplayIceServersShouldForceClientRelay(activeIceServers)
          ? "relay"
          : "all",
        bundlePolicy: "max-bundle",
        rtcpMuxPolicy: "require",
      });
      browserWebRtcDebug("peer-created", {
        clientId: nextClientId,
        iceTransportPolicy: browserDisplayIceServersShouldForceClientRelay(activeIceServers)
          ? "relay"
          : "all",
        iceServerCount: activeIceServers?.length ?? 0,
      });
      peer = nextPeer;
      peerRef.current = nextPeer;

      const videoTransceiver = nextPeer.addTransceiver("video", { direction: "recvonly" });
      preferVp8ReceiveCodec(videoTransceiver);
      nextPeer.ontrack = (event) => {
        browserWebRtcDebug("track-event", {
          clientId: nextClientId,
          trackKind: event.track?.kind ?? null,
          trackReadyState: event.track?.readyState ?? null,
          streamCount: event.streams.length,
          streamVideoTrackCounts: event.streams.map((stream) => stream.getVideoTracks().length),
        });
        if (
          abortController.signal.aborted ||
          failed ||
          peerRef.current !== nextPeer ||
          attachGenerationRef.current !== nextGeneration
        ) {
          return;
        }
        const streamWithVideo = event.streams.find((stream) => stream.getVideoTracks().length > 0);
        const track = event.track;
        const stream =
          streamWithVideo ??
          (track?.kind === "video" && track.readyState === "live"
            ? new MediaStream([track])
            : null);
        if (videoRef.current) {
          if (!stream || !browserStreamHasLiveVideoTrack(stream)) {
            browserWebRtcDebug("track-event-ignored", {
              clientId: nextClientId,
              hasStream: Boolean(stream),
              liveVideoTrack: stream ? browserStreamHasLiveVideoTrack(stream) : false,
            });
            return;
          }
          failed = false;
          trackReceivedRef.current = true;
          clearTrackTimeout();
          clearDisconnectedTimeout();
          videoRef.current.srcObject = stream;
          if (playbackAttemptedStreamRef.current !== stream) {
            playbackAttemptedStreamRef.current = stream;
            void videoRef.current.play().catch((error: unknown) => {
              if (videoRef.current?.srcObject === stream) {
                onErrorRef.current(error);
              }
            });
          }
          setVideoElementHasActiveStream(
            browserVideoElementHasRenderableStream(videoRef.current, stream),
          );
          attachedTrackStream = stream;
          setAttachedVideoStream({ generation: nextGeneration, stream });
          browserWebRtcDebug("track-attached", {
            clientId: nextClientId,
            videoReadyState: videoRef.current.readyState,
            videoWidth: videoRef.current.videoWidth,
            videoHeight: videoRef.current.videoHeight,
            streamVideoTrackCount: stream.getVideoTracks().length,
          });
          scheduleMediaHealthTimeout(nextPeer);
        }
      };
      nextPeer.onconnectionstatechange = () => {
        browserWebRtcDebug("connection-state", {
          clientId: nextClientId,
          connectionState: nextPeer.connectionState,
          iceConnectionState: nextPeer.iceConnectionState,
        });
        if (nextPeer.connectionState === "failed") {
          failWithNetworkUnreachable(nextPeer);
          return;
        }
        if (nextPeer.connectionState === "connected") {
          clearConnectivityTimeout();
          clearDisconnectedTimeout();
          scheduleMediaHealthTimeout(nextPeer);
        }
      };
      nextPeer.oniceconnectionstatechange = () => {
        browserWebRtcDebug("ice-connection-state", {
          clientId: nextClientId,
          connectionState: nextPeer.connectionState,
          iceConnectionState: nextPeer.iceConnectionState,
        });
        if (nextPeer.iceConnectionState === "failed") {
          failWithNetworkUnreachable(nextPeer);
          return;
        }
        if (
          nextPeer.iceConnectionState === "connected" ||
          nextPeer.iceConnectionState === "completed"
        ) {
          clearConnectivityTimeout();
          clearDisconnectedTimeout();
          scheduleMediaHealthTimeout(nextPeer);
          return;
        }
        if (nextPeer.iceConnectionState === "disconnected" && disconnectedTimeout === undefined) {
          disconnectedTimeout = window.setTimeout(
            () => failWithNetworkUnreachable(nextPeer),
            WEBRTC_DISCONNECTED_TIMEOUT_MS,
          );
        }
      };
      nextPeer.onicecandidate = (event) => {
        browserWebRtcDebug("local-ice-candidate", {
          clientId: nextClientId,
          candidate: event.candidate?.candidate ?? null,
        });
        if (abortController.signal.aborted || failed) {
          return;
        }
        handleLocalIceCandidate(
          event.candidate
            ? {
                candidate: event.candidate.candidate,
                sdpMid: event.candidate.sdpMid,
                sdpMLineIndex: event.candidate.sdpMLineIndex,
                usernameFragment: event.candidate.usernameFragment,
              }
            : null,
        );
      };

      const offer = await nextPeer.createOffer({
        offerToReceiveVideo: true,
        offerToReceiveAudio: false,
      });
      browserWebRtcDebug("offer-created", {
        clientId: nextClientId,
        hasSdp: Boolean(offer.sdp),
        sdpHasVp8: /VP8/i.test(offer.sdp ?? ""),
      });
      await nextPeer.setLocalDescription(offer);
      if (!nextPeer.localDescription?.sdp) {
        throw new Error("Failed to create a WebRTC display offer.");
      }
      const result = await withBrowserWebRtcNegotiationTimeout(
        api.browser.attachDisplay({
          threadId,
          browserId: activeBrowserId,
          sessionId: activeSessionId,
          tabId: attachTabId,
          clientId: nextClientId,
          offer: {
            type: "offer",
            sdp: nextPeer.localDescription.sdp,
          },
          ...browserDisplayAttachOptionalFields({
            userId,
            captureMode,
            viewport: activeViewport,
            devicePixelRatio: activeDevicePixelRatio,
          }),
        }),
        negotiationTimeoutMs,
        abortController.signal,
      );
      browserWebRtcDebug("attach-display-result", {
        clientId: nextClientId,
        status: result.display.status,
        reason: result.display.reason ?? null,
        detail: result.display.detail ?? null,
        answerType: result.answer.type,
        answerHasVp8: /VP8/i.test(result.answer.sdp),
      });

      if (abortController.signal.aborted) {
        return;
      }
      if (failed) {
        return;
      }
      onDisplayUpdatedRef.current(result.display);
      attachAccepted = true;
      flushLocalIceCandidates();
      await nextPeer.setRemoteDescription(result.answer);
      remoteDescriptionAcceptedRef.current = true;
      browserWebRtcDebug("remote-description-accepted", {
        clientId: nextClientId,
        connectionState: nextPeer.connectionState,
        iceConnectionState: nextPeer.iceConnectionState,
      });
      clearAttachDeadlineTimeout();
      await flushRemoteIceCandidates(nextPeer);
      if (abortController.signal.aborted || failed) {
        return;
      }
      if (result.display.status === "connected") {
        acceptedDisplay = result.display;
        scheduleConnectivityTimeout(nextPeer);
        scheduleMediaHealthTimeout(nextPeer);
      }
    }

    attach().catch((error: unknown) => {
      if (!abortController.signal.aborted && !failed) {
        if (browserErrorIsWebRtcNegotiationTimeout(error)) {
          failWithNegotiationTimeout();
          return;
        }
        scheduleReconnectOrFail(
          browserWebRtcAttachFailureDisplay({
            viewport: acceptedDisplay?.viewport ?? activeViewport,
            devicePixelRatio: acceptedDisplay?.devicePixelRatio ?? activeDevicePixelRatio,
            iceServers: acceptedDisplay?.iceServers ?? activeIceServers,
            error,
          }),
          peer ?? undefined,
          error,
        );
      }
    });

    return () => {
      abortController.abort();
      browserWebRtcDebug("effect-cleanup", {
        clientId: nextClientId,
        connectionState: peer?.connectionState ?? null,
        iceConnectionState: peer?.iceConnectionState ?? null,
      });
      clearAttachDeadlineTimeout();
      clearConnectivityTimeout();
      clearTrackTimeout();
      clearDisconnectedTimeout();
      clearReconnectTimeout();
      if (videoElement) {
        videoElement.srcObject = null;
      }
      if (attachGenerationRef.current === nextGeneration) {
        attachGenerationRef.current += 1;
        attachedTrackStream = null;
        setAttachedVideoStream(null);
        setVideoElementHasActiveStream(false);
      }
      if (peerRef.current === peer) {
        peerRef.current = null;
      }
      peer?.close();
    };
  }, [
    active,
    browserId,
    connectivityTimeoutMs,
    devicePixelRatioKey,
    environmentId,
    iceServersKey,
    maxReconnectAttempts,
    negotiationTimeoutMs,
    reconnectBaseDelayMs,
    reconnectGeneration,
    reconnectMaxDelayMs,
    captureMode,
    sessionId,
    tabPresenceKey,
    threadId,
    trackTimeoutMs,
    userId,
    viewportKey,
  ]);

  return (
    <video
      ref={videoRef}
      aria-label="Remote browser display"
      autoPlay
      className={cn(
        "pointer-events-none absolute inset-0 h-full w-full bg-background object-fill",
        videoClassState.visibilityClass,
        videoClassState.opacityClass,
      )}
      data-testid="remote-webrtc-browser-video"
      muted
      playsInline
      style={{
        aspectRatio: browserWebRtcVideoAspectRatio(viewport),
      }}
    />
  );
}
